import { QueryClient } from "@tanstack/react-query";
import { createRouter, Link } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

function DefaultError({ error, reset }: { error: any; reset: () => void }) {
  if (typeof console !== "undefined") console.error("[router]", error);
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-bold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We hit an unexpected error. You can try again or head back home.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={reset}
            className="rounded-full bg-foreground px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-background"
          >
            Try again
          </button>
          <Link
            to="/"
            className="rounded-full border border-border px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em]"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function DefaultNotFound() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-6xl font-display font-extrabold">404</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          We couldn't find that page.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex rounded-full bg-foreground px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-background"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}

export const getRouter = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    },
  });

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    // Prefetch route code + loader data on hover/focus so the next
    // navigation renders instantly. 30s stale window means repeated
    // hovers reuse the same in-flight/loaded data.
    defaultPreload: "intent",
    defaultPreloadDelay: 50,
    defaultPreloadStaleTime: 30_000,
    defaultErrorComponent: DefaultError,
    defaultNotFoundComponent: DefaultNotFound,
  });

  return router;
};
